import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import TopNav from './components/TopNav';
import {
  Login,
  FormChoice,
  AnesthesiaForm,
  ImplantForm,
  Success,
  Emergency,
  MainScreen,
  DataView,
  DentoalveolarSurgeryForm,
  PracticeSetup,
  Settings,
  NeedleStickInjuryForm,
  PatientInfoForm,
  ComplicationsMenu,
  OperationsMenu,
  ComplaintsMenu,
  SchedulingMenu,
  SimpleReportForm,
  EquipmentForm,
  ComplaintForm,
  SchedulingForm,
  AggressivePatientForm
} from './components';
import { doc, addDoc, collection, getDocs, query, where, setDoc } from 'firebase/firestore';
import { db, storage } from './firebase/firebaseConfig.js';
import { getAuth, signOut } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const initialAnesthesiaData = {
  sex: '',
  age: '',
  weight: '',
  dateOfSurgery: '',
  americanSocietyAnesthesiologistsClassification: '',
  anesthesiaType: '',
  anesthesiaProvider: '',
  surgicalTime: '',
  primaryProcedure: '',
  coMorbidities: '',
  intraPostOperativeEvents: '',
  intraPostCriticalEvents: '',
  intraPostAirwayPulmonaryEvents: '',
  intraPostCardiacEvents: '',
  intraPostMedicationTransfusionEvents: '',
  intraPostNeurologicalOtherEvents: '',
  intraPostEquipmentEnvironmentEvents: '',
  intraPostProcessEvents: '',
  intraPostOtherEvents: ''
};

const initialImplantData = {
  manufacturer: '',
  itemNumber: '',
  serialNumber: '',
  lotNumber: '',
  insertionDate: '',
  timeToFailure: '',
  insertionTorque: '',
  contextOfPlacement: '',
  tissueFeatures: '',
  graftingType: '',
  membrane: '',
  membraneType: '',
  healing: '',
  restoringPractitioner: '', 
  prostheticRestoration: '', 
  surgeonIssues: '',
  deviceIssues: '',
  sterilizationMethod: '',
  cyclesBeforeReplacement: '',
  diagnosisAtFailure: '',
  preplacementXray: null,
  removalXray: null
};

const initialDentoalveollarData = {
  jawFracture: '',
  alveolarOsteitis: '',
  sinusExposure: '',
  nerveDamage: '',
  retainedRootTip: '',
  nerveDamageType: '',
  damageAdjacentTeeth: '',
  lipLaceration: '',
  lipBurn: '',
  retainedInstrumentForeignBody: '',
}

const intitialNeedleStickInjuryData = {
  needleStickInjury: ''
}

function App() {
  const [step, setStep] = useState('login');
  const [navStack, setNavStack] = useState([]);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [formType, setFormType] = useState('');
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().split('T')[0]);
  const [implantData, setImplantData] = useState(initialImplantData);
  const [anesthesiaData, setAnesthesiaData] = useState(initialAnesthesiaData);
  const [dentoalveolarData, setDentoalveolarData] = useState(initialDentoalveollarData);
  const [needleStickInjuryData, setNeedleStickInjuryData] = useState(intitialNeedleStickInjuryData);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    practice: '',
    role: '',
    password: '',
    confirmPassword: ''
  });
  const [practiceProfile, setPracticeProfile] = useState({
    practice: '',
    officeAddress: '',
    officeCity: '',
    officeState: '',
    officeZipCode: '',
    officePhone: '',
  });
  const [patientInfo, setPatientInfo] = useState({
    patientID: '',
    phoneNumber: ''
  });
  const [userPractice, setUserPractice] = useState('');
  const [pendingToast, setPendingToast] = useState(null); // show toast after redirect

  useEffect(() => {
    const fetchUserPractice = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', user.email.toLowerCase())));
        if (!userDoc.empty) {
          setUserPractice(userDoc.docs[0].data().practice);
        }
      }
    };
    fetchUserPractice();
  }, []);

  const navigateTo = useCallback((nextStep) => {
    setNavStack((prev) => (step ? [...prev, step] : prev));
    setStep(nextStep);
  }, [step]);

  const goBack = useCallback(() => {
    setNavStack((prev) => {
      const copy = [...prev];
      const last = copy.pop();
      if (last) {
        setStep(last);
      }
      return copy;
    });
  }, []);

  const goHome = useCallback(() => {
    setNavStack([]);
    setStep('mainScreen');
  }, []);

  // Fire any deferred toast when we land on the home screen
  useEffect(() => {
    if (step === 'mainScreen' && pendingToast) {
      // Small delay to ensure the page has rendered
      const timer = setTimeout(() => {
        const { type = 'success', message } = pendingToast;

        if (message) {
          toast[type](message);
        }
        setPendingToast(null);
      }, 300); // Increased delay
      
      return () => clearTimeout(timer);
    }
  }, [step, pendingToast]);

  const handleLogin = useCallback(async () => {
    setIsUserLoggedIn(true);
  
    const auth = getAuth();
    const user = auth.currentUser;
  
    if (user) {
      const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', user.email.toLowerCase())));
      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data();
        setUserInfo({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          mobile: userData.mobile,
          practice: userData.practice,
          role: userData.role,
        });
      }
    }
  
    setStep('mainScreen');
  }, []);

  const handleFormChoice = useCallback((choice) => {
    setFormType(choice);
    setStep('patientInfo');
  }, []);

  const handleViewData = useCallback(() => {
    navigateTo('viewData');
  }, [navigateTo]);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        setNavStack([]);
        setStep('login'); 
      })
      .catch((error) => {
        console.error('Logout Error:', error);
      });
  };
  
  const handleNeedleStickInjuryChange = useCallback((e) => {
    setNeedleStickInjuryData(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handleAnesthesiaChange = useCallback((e) => {
    setAnesthesiaData(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handleImplantChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setImplantData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? (
        checked
          ? [...prevState[name], value]
          : prevState[name].filter(v => v !== value)
      ) : value
    }));
  }, []);

  const handleDentoalveolarChange = (event) => {
    const { name, type, checked } = event.target;
  
    const value = type === "checkbox" ? (checked ? "Yes" : "No") : event.target.value;
  
    setDentoalveolarData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  

  const handleFileChange = useCallback((event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      setImplantData(prevData => ({
        ...prevData,
        [fieldName]: file
      }));
    }
  }, []);

  const handleUserInfoChange = useCallback((e) => {
    setUserInfo(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handlePracticeSetup = async (practiceData) => {
    try {
      console.log('Setting up practice:', practiceData);
      alert('Practice profile created successfully.');
    } catch (error) {
      alert(`Setup failed: ${error.message}`);
    }
  };

  const handleEmergencySubmit = useCallback(async (emergencyData) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        toast.error('No user logged in');
        return;
      }
  
      const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', user.email.toLowerCase())));
      if (userDoc.empty) {
        toast.error('User data not found');
        return;
      }
      const userPractice = userDoc.docs[0].data().practice;
  
      const formData = {
        ...emergencyData,
        userEmail: user.email.toLowerCase(),
        practice: userPractice,
        completionDate
      };
  
      const emergencyCollection = 'Emergency';
      const emergencyCollectionRef = collection(db, emergencyCollection);
  
      await addDoc(emergencyCollectionRef, formData);
      setPendingToast({ type: 'success', message: 'Emergency report submitted' });
      setStep('mainScreen');
    } catch (error) {
      console.error("Error submitting emergency data: ", error);
      toast.error('Emergency submission failed');
    }
  }, [completionDate]);

  const handleFormSubmission = useCallback(async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const payload = e && e.payload ? e.payload : null;
  
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      alert('No user logged in');
      return;
    }
  
    const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', user.email.toLowerCase())));
    if (userDoc.empty) {
      alert('User data not found');
      return;
    }
    const userPractice = userDoc.docs[0].data().practice;
  
    let data;
    if (formType === 'Anesthesia') {
      data = { ...anesthesiaData };
    } else if (formType === 'Dental Implant') {
      data = { ...implantData };
    } else if (formType === 'Dentoalveolar Surgery') {
      data = { ...dentoalveolarData };
    } else if (formType === 'Needle Stick Injury') {
      data = { ...needleStickInjuryData };
    } else {
      // Operational categories submit their own structured payload
      data = payload || { category: formType };
    }
    
    let parentCollection = 'Complications';
    let parentDocId = 'formRefDoc';
    let subcollectionName = formType; 

    if (e && e.specialCollection === 'AggressivePatientInteractions') {
      parentCollection = 'AggressivePatientInteractions';
      parentDocId = 'root';
      subcollectionName = 'Entries';
    } else if (formType.startsWith('Equipment') || formType.startsWith('Complaint') || formType.startsWith('Scheduling')) {
      parentCollection = 'Operations';
      parentDocId = 'opsRefDoc';
      // Clean up the subcollection name - remove spaces and special characters
      subcollectionName = formType.replace(/\s+/g, '_').replace(/[\/\-]/g, '_');
    } else {
      // keep defaults for clinical complications
    }

    const extendedData = {
      ...data,
      userEmail: user.email.toLowerCase(),
      practice: userPractice,
      completionDate,
      patientID: patientInfo.patientID,
      patientPhoneNumber: patientInfo.phoneNumber
    };

    const parentDocRef = doc(db, parentCollection, parentDocId);
    const subcollectionRef = collection(parentDocRef, subcollectionName);
  
    try {
      const uploadFile = async (file, path) => {
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      };
  
      if (formType === 'Dental Implant') {
        if (implantData.preplacementXray) {
          const preplacementXrayURL = await uploadFile(
            implantData.preplacementXray,
            `Preplacement x-rays/${Date.now()}_${implantData.preplacementXray.name}`
          );
          extendedData.preplacementXray = preplacementXrayURL;
        }
        if (implantData.removalXray) {
          const removalXrayURL = await uploadFile(
            implantData.removalXray,
            `Removal x-rays/${Date.now()}_${implantData.removalXray.name}`
          );
          extendedData.removalXray = removalXrayURL;
        }
      }
  
      await addDoc(subcollectionRef, extendedData);

      setPendingToast({ type: 'success', message: 'Form submitted successfully.' });
      if (formType === 'Anesthesia') {
        setAnesthesiaData(initialAnesthesiaData);
      } else if (formType === 'Dental Implant') {
        setImplantData(initialImplantData);
      } else if (formType === 'Dentoalveolar Surgery') {
        setDentoalveolarData(initialDentoalveollarData);
      } else if (formType === 'Needle Stick Injury') {
        setNeedleStickInjuryData(intitialNeedleStickInjuryData);
      }
    } catch (error) {
      toast.error('Submission failed');
    }
  
    setStep('mainScreen');
  }, [formType, anesthesiaData, implantData, dentoalveolarData, needleStickInjuryData, completionDate, patientInfo]);

  const handleSuccessDataSubmission = useCallback(async (submissionData) => {
    try {
      const { patientId, practice, userEmail } = submissionData;
  
      const entryRef = collection(db, 'Success', 'successRefDoc', 'Anesthesia');
      await setDoc(doc(entryRef, patientId), {
        userEmail,
        patientId,
        practice,
        completionDate: new Date().toISOString().split('T')[0],
      });
  
      setPendingToast({ type: 'success', message: 'Success recorded' });
      setStep('mainScreen');
    } catch (error) {
      toast.error('Failed to record success');
    }
  }, [setStep]);
  
  const handleSignup = async (userInfo) => {
    const { firstName, lastName, mobile, role, practice, email, password, confirmPassword} = userInfo;
  
    if (password !== confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
  
    try {
      await addDoc(collection(db, 'users'), {
        firstName,
        lastName,
        mobile,
        role,
        practice,
        email: email.toLowerCase(),
        status: 'Pending'
      });
      alert('Signup successful.');
    } catch (error) {
      alert(`Signup failed: ${error.message}`);
    }
  };

  return (
    <div className="App">
      {step !== 'login' && (
        <TopNav
          onBack={step !== 'mainScreen' ? goBack : null}
          onHome={step !== 'mainScreen' ? goHome : null}
          onSettings={() => navigateTo('settings')}
          onOutcomes={() => navigateTo('viewData')}
          onLogout={handleLogout}
        />
      )}
      

      {step === 'login' && (
        <Login
          userInfo={userInfo}
          handleUserInfoChange={handleUserInfoChange}
          handleLogin={handleLogin}
          handleSignup={handleSignup}
          practiceProfile={practiceProfile}
          setPracticeProfile={setPracticeProfile}
          handlePracticeSetup={handlePracticeSetup}
          isUserLoggedIn={isUserLoggedIn}
          handleViewData={handleViewData}
          setStep={setStep}
        />
      )}

      {step === 'viewData' && (
        <DataView practice={userInfo.practice} goBack={() => {setStep('login');}} />
      )}

      {step === 'mainScreen' && (
        <MainScreen setStep={navigateTo} />
      )}

      {step === 'chooseForm' && (
        <FormChoice handleFormChoice={handleFormChoice} />
      )}

      {step === 'complicationsMenu' && (
        <ComplicationsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('patientInfo'); }}
        />
      )}

      {step === 'operationsMenu' && (
        <OperationsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'complaintsMenu' && (
        <ComplaintsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'schedulingMenu' && (
        <SchedulingMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'emergencyPatientInfo' && (
        <PatientInfoForm
          onSubmit={(info) => {
            setPatientInfo(info);
            setStep('emergencyCheck');
          }}
        />
      )}

      {step === 'emergencyCheck' && (
        <Emergency handleEmergencySubmit={handleEmergencySubmit} setStep={setStep}/>
      )}

      {step === 'aggressivePatient' && (
        <AggressivePatientForm
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload, specialCollection: 'AggressivePatientInteractions' })}
        />
      )}

      {step === 'fillForm' && formType === 'Anesthesia' && (
        <AnesthesiaForm
          anesthesiaData={{...anesthesiaData}}
          handleAnesthesiaChange={handleAnesthesiaChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType === 'Dental Implant' && (
        <ImplantForm
          implantData={{...implantData}}
          handleImplantChange={handleImplantChange}
          handleFileChange={handleFileChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType === 'Dentoalveolar Surgery' && (
        <DentoalveolarSurgeryForm
          dentoalveolarData={dentoalveolarData}
          handleDentoalveolarChange={handleDentoalveolarChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType === 'Needle Stick Injury' && (
        <NeedleStickInjuryForm
          handleNeedleStickInjuryChange={handleNeedleStickInjuryChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType.startsWith('Equipment') && (
        <EquipmentForm
          category={formType}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload })}
        />
      )}

      {step === 'fillForm' && formType.startsWith('Complaint') && (
        <ComplaintForm
          category={formType}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload })}
        />
      )}

      {step === 'fillForm' && formType.startsWith('Scheduling') && (
        <SchedulingForm
          category={formType}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload })}
        />
      )}

      {step === 'submitData' && (
        <Success 
          setCompletionDate={setCompletionDate}
          handleSuccessDataSubmission={handleSuccessDataSubmission}
          setStep={setStep}
        />
      )}
      
      {step === 'patientInfo' && (
        <PatientInfoForm
          onSubmit={(info) => {
            setPatientInfo(info);
            setStep('fillForm');
          }}
        />
      )}

      {step === 'practiceSetup' && (<PracticeSetup/>)}
      {step === 'settings' && (
        <div>
          {console.log('Rendering Settings with step:', step, 'userInfo:', userInfo)}
          <Settings setStep={setStep} userInfo={userInfo}/>
        </div>
      )}
    </div>
  );
}

export default App;
